import React, { useEffect } from 'react'
import {
  Link,
  Redirect,
  RouteComponentProps,
  useHistory,
} from 'react-router-dom'
import gql from 'graphql-tag'
import { useQuery, useMutation } from '@apollo/react-hooks'
import {
  Container,
  Box,
  Avatar,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { AppBar, Toolbar } from '@mui/material'
import { Menu, MenuItem } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import Badge from '@mui/material/Badge'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import MailIcon from '@mui/icons-material/Mail'
import HomeIcon from '@mui/icons-material/Home'
import ExportIcon from '@mui/icons-material/FileDownload'
import AddIcon from '@mui/icons-material/Add'
import { TbReport } from 'react-icons/tb'
import { useAuthentication } from '../hooks/useAuthentication'
import { hasReportsAccess } from '../utils'
import {
  CREATE_VOUCHER,
  VoucherCreateInputMap,
  GET_USERS_LIST,
} from './Vouchers/gql'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import { User } from '../types'

export const IMPERSONATE_USER = gql`
  mutation impersonate($id: String!) {
    impersonate(id: $id) {
      token
      name
      user {
        first_name
        last_name
        email
        role
        majlis
        department {
          id
          name
        }
      }
    }
  }
`

export const GET_NOTIFICATION_COUNT = gql`
  query getNotificationCount {
    notificationCount
  }
`

const Header: React.FunctionComponent<RouteComponentProps> = () => {
  const { authState, signOut, signIn } = useAuthentication()
  const history = useHistory()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const notificationCountQuery = useQuery(GET_NOTIFICATION_COUNT, {
    context: { headers: { authorization: authState.authToken } },
    fetchPolicy: 'network-only', // goes to server, not cache
  })
  const [newVoucher, { loading, data }] = useMutation(CREATE_VOUCHER)
  const [impersonate, { loading: impersonateLoading }] =
    useMutation(IMPERSONATE_USER)
  // const classes = useStyles()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [notification, setNotification] = React.useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'info' | 'warning'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const usersResult = useQuery(GET_USERS_LIST)
  const [users, setUsers] = React.useState<Array<any>>([])
  useEffect(() => {
    notificationCountQuery.refetch()
  }, [notificationCountQuery])

  // Update users when query result changes
  useEffect(() => {
    if (usersResult.data?.users) {
      let users = usersResult.data.users
      if (authState.authUser.role === 'LOCAL_PRESIDENT') {
        // filter out users from other majlis
        users = users.filter(
          (user: User) => user.majlis === authState.authUser.majlis
        )
      }
      setUsers(users)
    }
  }, [usersResult.data, authState.authUser.role, authState.authUser.majlis])

  const handleImpersonate = async (userId: string) => {
    try {
      const result = await impersonate({
        variables: { id: userId },
        context: { headers: { authorization: authState.authToken } },
      })

      if (result.data?.impersonate?.token) {
        // Replace the JWT token in localStorage and update auth state
        signIn(result.data.impersonate.token)
        // Close the menu
        handleProfileMenuClose()
        // Show a success notification
        setNotification({
          open: true,
          message:
            'You are now impersonating ' +
            result.data.impersonate.user.first_name +
            ' ' +
            result.data.impersonate.user.last_name,
          severity: 'info',
        })
        console.log(JSON.stringify(result.data.impersonate.user))
        // Navigate to home page and reload
        setTimeout(() => {
          history.push('/panel')
          window.location.reload()
        }, 1500) // Wait 1.5 seconds for notification to be visible
      }
    } catch (error) {
      console.error('Impersonation failed:', error)
      // Show an error notification
      setNotification({
        open: true,
        message: 'Impersonation failed. Please try again.',
        severity: 'error',
      })
    }
  }

  const Impersonate = () => {
    return (
      <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
        <InputLabel id="impersonate-select-label">
          {impersonateLoading ? 'Impersonating...' : 'Impersonate User'}
        </InputLabel>
        <Select
          labelId="impersonate-select-label"
          id="impersonate-select"
          label={impersonateLoading ? 'Impersonating...' : 'Impersonate User'}
          disabled={impersonateLoading}
          onChange={(event) => {
            const userId = event.target.value as string
            if (userId) {
              handleImpersonate(userId)
            }
          }}
          value=""
          sx={{
            '& .MuiSelect-select': {
              padding: '8px 12px',
              fontSize: '0.875rem',
            },
          }}
          endAdornment={
            impersonateLoading ? (
              <CircularProgress size={16} sx={{ marginRight: 1 }} />
            ) : null
          }
        >
          {users.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {user.first_name} {user.last_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  const isMenuOpen = Boolean(anchorEl)
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }))
  }
  const handleSaveAsDraft = (
    departmentOverride?: string,
    payToOverride?: string
  ) => {
    if (loading) {
      return
    }
    const today = new Date()
    const fiscalYear =
      today.getMonth() + 1 >= 7 ? today.getFullYear() + 1 : today.getFullYear()

    const voucher = {
      description: '',
      department: departmentOverride || authState.authUser.department_id,
      total_amount: 0,
      receivers_account_number: '',
      financial_year: fiscalYear,
      created_by: authState.authUser.id,
      pay_to: payToOverride || authState.authUser.first_name + ' ' + authState.authUser.last_name,
      claimed_by: authState.authUser.id,
      voucher_cost_category: [] as any,
    }
    const voucherInput = VoucherCreateInputMap(voucher)
    newVoucher({
      variables: { input: voucherInput },
    })
  }

  const handleCreateVoucherClick = () => {
    handleSaveAsDraft()
  }

  const count = notificationCountQuery.data?.notificationCount ?? 0

  return (
    <div>
      <AppBar
        position="static"
        sx={{
          backgroundColor:
            window.location.hostname !== 'vhs.ahmadiyya.se'
              ? 'green'
              : undefined,
        }}
      >
        <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
          <Toolbar
            variant="dense"
            disableGutters
            sx={{ minHeight: { xs: '48px', sm: '56px' } }}
          >
            <MenuItem
              component={Link}
              to="/panel"
              sx={{
                minWidth: 'auto',
                px: { xs: 1, sm: 2 },
                '&:hover': { backgroundColor: 'transparent' },
              }}
            >
              <Avatar
                alt="logo"
                src="/logo.svg"
                sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}
              />
              {!isMobile && (
                <Box
                  component="span"
                  ml={1}
                  mr={3}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  Voucher Handling System
                </Box>
              )}
            </MenuItem>

            {isMobile ? (
              // Mobile layout with icons
              <>
                <IconButton
                  component={Link}
                  to="/panel"
                  color="inherit"
                  sx={{ mx: 0.5 }}
                  title="Dashboard"
                >
                  <HomeIcon />
                </IconButton>
                <IconButton
                  onClick={handleCreateVoucherClick}
                  color="inherit"
                  sx={{ mx: 0.5 }}
                  title="Create Voucher"
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress color="inherit" size="1.25rem" />
                  ) : (
                    <AddIcon />
                  )}
                </IconButton>
                {hasReportsAccess(authState.authUser.department?.name || '') && (
                  <IconButton
                    component={Link}
                    to="/panel/reports"
                    color="inherit"
                    sx={{ mx: 0.5 }}
                    title="Reports"
                  >
                    <TbReport />
                  </IconButton>
                )}
                {authState.authUser.majlis === 'NATIONAL' &&
                  authState.authUser.role === 'AMIN' && (
                    <IconButton
                      component={Link}
                      to="/panel/export"
                      color="inherit"
                      sx={{ mx: 0.5 }}
                      title="Export Vouchers"
                    >
                      <ExportIcon />
                    </IconButton>
                  )}
              </>
            ) : (
              // Desktop layout with text
              <>
                <MenuItem component={Link} to="/panel">
                  Dashboard
                </MenuItem>
                <MenuItem onClick={handleCreateVoucherClick}>
                  <ListItemText>Create Voucher</ListItemText>
                  {loading ? (
                    <ListItemIcon sx={{ marginLeft: '0.5rem' }}>
                      <CircularProgress color="warning" size="1.25rem" />
                    </ListItemIcon>
                  ) : null}
                </MenuItem>
                {authState.authUser.majlis === 'NATIONAL' &&
                  authState.authUser.role === 'AMIN' && (
                    <MenuItem component={Link} to="/panel/export">
                      Export Vouchers
                    </MenuItem>
                  )}
                {hasReportsAccess(authState.authUser.department?.name || '') && (
                  <MenuItem component={Link} to="/panel/reports">
                    Reports
                  </MenuItem>
                )}
              </>
            )}

            <Box flexGrow="1" />
            <div>
              <IconButton
                component={Link}
                to={{
                  pathname: '/panel/inbox',
                  state: { refresh: true }, // trigger inbox refresh
                }}
                onClick={() => notificationCountQuery.refetch()}
                aria-label={`show ${count} new mails`}
                color="inherit"
                sx={{ mx: { xs: 0.5, sm: 1 } }}
              >
                <Badge badgeContent={count} color="secondary">
                  <MailIcon />
                </Badge>
              </IconButton>
              <IconButton
                edge="end"
                aria-label="account of current user"
                aria-controls="user-menu"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{ mx: { xs: 0.5, sm: 1 } }}
              >
                <AccountCircleIcon />
              </IconButton>
            </div>
          </Toolbar>
        </Container>
      </AppBar>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        id="user-menu"
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={isMenuOpen}
        onClose={handleProfileMenuClose}
      >
        <MenuItem>
          {authState.authUser.first_name} {authState.authUser.last_name}
        </MenuItem>
        {(authState.authUser.role === 'ADMIN' ||
          authState.authUser.site_admin) && (
            <MenuItem component={Link} to="/admin/users">
              Admin
            </MenuItem>
          )}
        {(authState.authUser.role === 'LOCAL_PRESIDENT' ||
          authState.authUser.role === 'AMIR') && (
            <MenuItem>
              <Impersonate />
            </MenuItem>
          )}
        <MenuItem onClick={signOut}>Log out</MenuItem>
      </Menu>
      {loading === false && data ? (
        <Redirect to={`/panel/vouchers/${data.createVoucher.id}/edit`} />
      ) : null}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default Header
